import {
  IconToggle,
  IconToggleOption,
} from "@/design-system/IconToggle/IconToggle";
import {
  CompareSubsection,
  trackChangedCompareMode,
} from "@/events/compareMixpanel";
import { useTranslation } from "@repo/shared/i18n";
import { useState } from "react";

export type Mode = "list" | "grouped" | "compact";

const MODE_STORAGE_KEY = "compareMode";

const isMode = (value: string | null): value is Mode =>
  value === "list" || value === "grouped" || value === "compact";

/**
 * The compare view mode, persisted to localStorage — one preference shared by
 * the desktop page and the mobile screen (each mounts its own copy).
 */
export const usePersistedMode = () => {
  const [mode, setMode] = useState<Mode>(() => {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    return isMode(stored) ? stored : "list";
  });
  const setPersistedMode = (next: Mode) => {
    setMode(next);
    localStorage.setItem(MODE_STORAGE_KEY, next);
  };
  return [mode, setPersistedMode] as const;
};

/**
 * The persisted mode plus the tracked change handler — the desktop page and
 * the mobile screen previously each carried this pair.
 */
export const useCompareMode = ({
  subsection,
  playerCount,
}: {
  /** Which host the toggle is in — stamped on the mode-change event. */
  subsection: CompareSubsection;
  playerCount: number;
}) => {
  const [mode, setMode] = usePersistedMode();
  const handleModeChange = (next: Mode) => {
    setMode(next);
    // IconToggle fires onChange on re-click of the active option too — only a
    // real switch is a mode change.
    if (next === mode) return;
    trackChangedCompareMode({
      subsection,
      value: next,
      player_count: playerCount,
    });
  };
  return [mode, handleModeChange] as const;
};

// Icons only; the accessible label is translated where the toggle renders.
const OPTION_ICONS: { value: Mode; icon: React.ReactNode }[] = [
  {
    value: "list",
    icon: (
      <span className="flex flex-col gap-[2.5px]">
        <span className="h-[4px] w-[13px] rounded-[2px] bg-current" />
        <span className="h-[4px] w-[13px] rounded-[2px] bg-current" />
      </span>
    ),
  },
  {
    value: "grouped",
    icon: (
      <span className="grid grid-cols-2 gap-[2px]">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="size-[6px] rounded-[1px] bg-current" />
        ))}
      </span>
    ),
  },
  {
    value: "compact",
    icon: (
      <span className="grid grid-cols-3 gap-[2px]">
        {[0, 1, 2, 3, 5, 6, 7, 8, 9].map((i) => (
          <span key={i} className="size-[4px] rounded-[1px] bg-current" />
        ))}
      </span>
    ),
  },
];

/** Segmented list / grouped / compact toggle for the PlayStyles section. */
export const ModeToggle = ({
  mode,
  onChange,
}: {
  mode: Mode;
  onChange: (mode: Mode) => void;
}) => {
  const { t } = useTranslation("compare");
  const options: IconToggleOption<Mode>[] = OPTION_ICONS.map((option) => ({
    ...option,
    label: t(`mode.${option.value}` as const),
  }));
  return <IconToggle value={mode} options={options} onChange={onChange} />;
};
